/**
 * Mana Locals Admin — dashboard boundary schemas.
 *
 * THESE ARE THE READS THE DASHBOARD MAKES THAT NO OTHER MODULE ALREADY OWNS:
 *
 *   - the tenant's SETUP COMPLETENESS — M01's onboarding readiness projection
 *     (`GET /api/v1/tenants/current/onboarding`);
 *   - the tenant's TEAM — M10's staff memberships
 *     (`GET /api/v1/workforce/staff-memberships`);
 *   - the platform's OPERATIONS CONTROL CENTRE — M63's backlog and vendor summary
 *     (`GET /api/v1/operations/backlog`,
 *     `GET /api/v1/operations/vendors/{tenantId}/summary`).
 *
 * EVERY OTHER READ THE DASHBOARD MAKES IS A READ ANOTHER MODULE ALREADY OWNS, and
 * those schemas are IMPORTED, not restated: `OfferListResponseSchema` and
 * `CanonicalProductListResponseSchema` from `catalog.ts`, `StockBalanceListResponseSchema`
 * and `StockMovementListResponseSchema` from `inventory.ts`,
 * `MediaAssetListResponseSchema` from `media.ts`, `EntitlementsResponseSchema` from
 * `foundation.ts`, `TenantListResponseSchema` from `tenants.ts`. Restating a shape
 * here would create a second place for it to be wrong.
 *
 * Nothing in this file is an authorization statement. Zod validates SHAPE only.
 */
import { z } from 'zod';

import { CollectionResponseSchema, IsoDateTimeSchema, ResponseMetaSchema, UuidSchema } from '@/api/schemas/common';

/* ------------------------------------------------------------------------------------------------
 * The tenant's setup completeness — M01's onboarding readiness projection.
 * ---------------------------------------------------------------------------------------------- */

/**
 * How one setup category stands.
 *
 * `not_supported` is NOT `missing`. A category the platform does not implement cannot be
 * completed by the tenant, so it must not be counted as something the tenant still owes — and
 * the API states the reason. The dashboard prints that reason rather than hiding the category.
 */
export const OnboardingCategoryStatusSchema = z.enum(['complete', 'missing', 'not_supported']);

export const OnboardingCategorySchema = z.object({
  category: z.string().min(1),
  status: OnboardingCategoryStatusSchema,
  /** Why the category is unsupported. Present only for `not_supported`. */
  reason: z.string().min(1).optional(),
});

export const OnboardingReadinessSchema = z.object({
  tenantId: z.string().min(1),
  /** Whether the tenant's setup is complete. */
  overall: z.enum(['complete', 'incomplete']),
  /** How many categories are still missing. */
  missingCount: z.number().int().min(0),
  categories: z.array(OnboardingCategorySchema),
});

export const OnboardingReadinessResponseSchema = z.object({
  data: OnboardingReadinessSchema,
  meta: ResponseMetaSchema,
});

export type OnboardingReadiness = z.infer<typeof OnboardingReadinessSchema>;
export type OnboardingCategory = z.infer<typeof OnboardingCategorySchema>;

/* ------------------------------------------------------------------------------------------------
 * The tenant's team — M10's staff memberships.
 * ---------------------------------------------------------------------------------------------- */

export const StaffMembershipStatusSchema = z.enum(['active', 'offboarded']);

export const StaffMembershipSchema = z
  .object({
    id: UuidSchema,
    tenantId: UuidSchema,
    accountId: UuidSchema,
    functionCodes: z.array(z.string()),
    reportsToStaffMembershipId: UuidSchema.nullable(),
    skillCodes: z.array(z.string()),
    locationIds: z.array(z.string()),
    status: StaffMembershipStatusSchema,
    offboardedAt: IsoDateTimeSchema.nullable(),
    version: z.number().int().min(1),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .passthrough();

export const StaffMembershipListResponseSchema = CollectionResponseSchema(StaffMembershipSchema);

export type StaffMembership = z.infer<typeof StaffMembershipSchema>;

/* ------------------------------------------------------------------------------------------------
 * The platform's operations control centre — M63.
 * ---------------------------------------------------------------------------------------------- */

/** The four queues M63's backlog counts. The API's own closed vocabulary. */
export const BacklogQueueClassSchema = z.enum([
  'admin_action_pending_authorization',
  'admin_action_outcome_unconfirmed',
  'approval_case_pending',
  'approval_case_expiring',
]);

export const BacklogQueueSchema = z.object({
  queueClass: BacklogQueueClassSchema,
  openCount: z.number().int().min(0),
  oldestOpenedAt: IsoDateTimeSchema.optional(),
  /** The policy reference that bounds the queue. Absent when no policy names one. */
  thresholdRef: z
    .object({ kind: z.string().min(1), ref: z.string().min(1) })
    .optional(),
});

export const OperationsBacklogSchema = z.object({
  /** The instant the counts were taken. */
  asOf: IsoDateTimeSchema,
  queues: z.array(BacklogQueueSchema),
  /** Always `internal` for this projection. */
  sensitivity: z.enum(['internal', 'restricted']),
});

export const OperationsBacklogResponseSchema = z.object({
  data: OperationsBacklogSchema,
  meta: ResponseMetaSchema,
});

/** The vendor-scoped operations projection for one affected tenant (VND-60). */
export const VendorOperationsSummarySchema = z.object({
  tenantId: z.string().min(1),
  window: z.object({ from: IsoDateTimeSchema, to: IsoDateTimeSchema }),
  adminActionsByClass: z.object({
    vendor: z.number().int().min(0),
    moderation: z.number().int().min(0),
    finance: z.number().int().min(0),
    incident: z.number().int().min(0),
    access: z.number().int().min(0),
    release: z.number().int().min(0),
  }),
  pendingAuthorizationCount: z.number().int().min(0),
  outcomeUnconfirmedCount: z.number().int().min(0),
  openApprovalCaseCount: z.number().int().min(0),
  lastAdminActionAt: IsoDateTimeSchema.optional(),
  auditTrailPresent: z.boolean(),
  configurationEvidenceRefs: z.array(z.string()),
  sensitivity: z.enum(['internal', 'restricted']),
});

export const VendorOperationsSummaryResponseSchema = z.object({
  data: VendorOperationsSummarySchema,
  meta: ResponseMetaSchema,
});

export type BacklogQueueClass = z.infer<typeof BacklogQueueClassSchema>;
export type BacklogQueue = z.infer<typeof BacklogQueueSchema>;
export type OperationsBacklog = z.infer<typeof OperationsBacklogSchema>;
export type VendorOperationsSummary = z.infer<typeof VendorOperationsSummarySchema>;
